import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronRightIcon,
    MoneySendIcon,
    RefreshCircleIcon,
    TrendDownIcon,
    TrendUpIcon
} from '../../../../components/icons';
import Button from '../../../../components/button/Button';
import CustomTable from '../../../../components/CustomTable';
import { TreasuryTransactionsSchema } from './schema';
import SummaryCard from './Components/SummaryCard';
import { Avatar } from 'antd';
import { UnlinkAccount } from './Components/UnlinkModal';
import { FilterParams } from '../../../../interfaces/Filter';
import CustomCarousel from '../../../../components/CustomCarousel';
import { useTreasuryTransactions } from '../../../../customhooks/treasury/useTreasuryTransactions';
import { useGetBank } from '../../../../customhooks/treasury/useGetBank';
import TransactionTableMobileCard from './Components/TransactionTableMobileCard';
import { Bank } from '../../../../customhooks/useBankLogos';
import { TreasuryTransaction } from '../../../../interfaces/Treasury';
import TreasuryTransactionDetails from '../Overview/TreasuryTransactionDetails';
import NoTransactionsIcon from '../../../../components/icons/NoTransactionsIcon';
import dayjs from 'dayjs';
import FilterSingleAccounts from './Components/FilterSingleAccounts';
import { getBanks } from 'banks-ng';

const TransactionsDetails = () => {
    const navigate = useNavigate();

    const { id } = useParams();

    const [openModal, setOpenModal] = useState(false);
    const [viewDetails, setViewDetails] = useState(false);
    const [
        selectedTransaction,
        setSelectedTransaction
    ] = useState<TreasuryTransaction | null>(null);

    const [transactionFilterOptions, setTransactionFilterOptions] = useState<
        FilterParams
    >({
        page: 0,
        size: 10
    });

    const { account, refetch, summary } = useGetBank(
        Number(id),
        transactionFilterOptions
    );

    const {
        isLoading: isTransactionsLoading,
        transactionsList,
        invalidateTransactionsCache: fetchTransactions,
        isFetching: isFetchingTransactions,
        transactionPaging
    } = useTreasuryTransactions(String(id), transactionFilterOptions);

    const handleRefresh = () => {
        refetch();
        fetchTransactions();
    };

    const handleViewTransactions = (record: TreasuryTransaction) => {
        if (record) {
            setSelectedTransaction(record);
            setViewDetails(true);
        }
    };

    const BankResult: Bank[] = getBanks();

    const bankLookupByName = React.useMemo(() => {
        const map = new Map<string, Bank>();
        BankResult.forEach((bank: Bank) => {
            map.set(bank.name.toLowerCase(), bank);
        });
        return map;
    }, [BankResult]);

    const bank = account?.name
        ? bankLookupByName.get(account.name.toLowerCase())
        : undefined;

    const summaryList = [
        {
            isBank: true,
            bankName: account?.name || '-',
            accountNumber: account?.accountNumber || '-',
            label: 'Current balance',
            amount: summary?.total || '0',
            trend: 'positive',
            percent: 0,
            currency: 'NGN',
            logo: (
                <Avatar
                    size={24}
                    src={bank?.logo || ''}
                    style={{
                        border: '1px solid #D0D5DD',
                        background: 'transparent'
                    }}
                />
            ),
            message: 'Combined balance across all your linked accounts.'
        },
        {
            label: 'Opening Balance',
            amount: summary?.opening || 0,
            trend: 'negative',
            percent: 0,
            currency: 'NGN',
            logo: (
                <div className='w-[32px] h-[32px] flex justify-center items-center rounded-[50%] bg-[#ECFDF3]'>
                    <TrendDownIcon />
                </div>
            ),
            message: 'Your consolidated balance at the start of the day'
        },
        {
            label: 'Closing Balance',
            amount: summary?.closing || 0,
            trend: 'positive',
            percent: 0,
            currency: 'NGN',
            logo: (
                <div className='w-[32px] h-[32px] flex justify-center items-center rounded-[50%] bg-provincialPink'>
                    <TrendUpIcon />
                </div>
            ),
            message: 'Your consolidated balance at the end of the day'
        }
    ];

    const noTransactions =
        !transactionsList?.transactions?.length &&
        !isFetchingTransactions &&
        !isTransactionsLoading;

    return (
        <div>
            {noTransactions ? (
                <div className='w-full flex justify-center items-center lg:h-[80vh] h-[80dvh] bg-white lg:bg-transparent flex-col gap-2'>
                    <NoTransactionsIcon />
                    <h3 className='text-[20px] font-semibold text-center text-ebony'>
                        No transaction data available
                    </h3>
                    <Button
                        text='Refresh Transactions'
                        className='mt-3'
                        size='sm'
                        isLoading={
                            isFetchingTransactions || isTransactionsLoading
                        }
                        leftIcon={<RefreshCircleIcon color='#ffffff' />}
                        onClick={fetchTransactions}
                    />
                </div>
            ) : (
                <>
                    <div className='flex items-center justify-between lg:pr-[52px] '>
                        <div className='flex gap-3 bg-white mb-[32px] lg:mb-0 lg:bg-transparent w-full lg:w-fit pt-[24px] p-4 lg:p-0 lg:pt-0 items-center'>
                            <p
                                className='text-gray800 text-sm cursor-pointer hover:underline hover:font-medium'
                                onClick={() => navigate(-1)}
                            >
                                Accounts
                            </p>
                            <ChevronRightIcon />
                            <p className='text-primary500 font-medium text-sm items-center'>
                                Account Overview
                            </p>
                        </div>
                        <div className='flex items-center gap-4 mr-4'>
                            <button
                                onClick={handleRefresh}
                                className='flex items-center gap-2 text-gray200 text-[14px] font-semibold leading-[20px]'
                            >
                                <RefreshCircleIcon color='#475467' /> Refresh
                                account
                            </button>
                        </div>
                    </div>
                    <div className=' mt-10 flex gap-4 mb-6  w-full justify-between items-center'>
                        <h1 className=' text-xl font-medium'>
                            {account?.accountName} ({account?.accountNumber})
                        </h1>

                        <FilterSingleAccounts
                            {...{
                                transactionFilterOptions,
                                setTransactionFilterOptions
                            }}
                        />
                    </div>

                    <div className='hidden lg:flex gap-4 pb-4'>
                        {summaryList?.map((item) => (
                            <SummaryCard key={item.label} item={item} />
                        ))}
                    </div>

                    <div className='lg:hidden px-4'>
                        <CustomCarousel
                            data={summaryList}
                            component={<SummaryCard />}
                        />

                        <div>
                            <div className='flex items-center gap-4 mt-8 mb-2'>
                                <Button
                                    onClick={handleRefresh}
                                    className='flex items-center gap-2 text-gray200 text-[14px] font-semibold leading-[20px]'
                                    variant='secondaryOutline'
                                    text='Refresh'
                                    leftIcon={
                                        <RefreshCircleIcon color='#475467' />
                                    }
                                    style={{ width: '100%' }}
                                />
                                <Button
                                    text='Make payment'
                                    leftIcon={<MoneySendIcon color='#FFFFFF' />}
                                    onClick={() =>
                                        navigate('/treasury/payment')
                                    }
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <Button
                                text='Unlink Account'
                                variant='errorSecondary'
                                onClick={() => setOpenModal(true)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    <div className='pt-8 pb-4'>
                        <p className='text-lg font-medium text-ebony'>
                            Transactions
                        </p>
                        <span className='text-sm text-[#98A2B3] font-semibold'>
                            Last updated:{' '}
                            {dayjs(Date.now()).format('MMM DD, YYYY, HH:MM A')}
                        </span>
                    </div>
                    <div className='mt-4'>
                        <div className='bg-white h-fit lg:rounded-lg'>
                            <div className='flex justify-between w-full items-center p-4'>
                                <h3 className=' text-ebony text-[18px] font-semibold '>
                                    All transactions
                                </h3>

                                <div className='hidden lg:flex gap-2'>
                                    <Button
                                        text='Unlink Account'
                                        variant='errorSecondary'
                                        onClick={() => setOpenModal(true)}
                                        size='sm'
                                    />
                                </div>
                            </div>

                            <CustomTable
                                data={transactionsList?.transactions ?? []}
                                loadingData={
                                    isTransactionsLoading ||
                                    isFetchingTransactions
                                }
                                updateCurrentData={(record) =>
                                    handleViewTransactions(record)
                                }
                                onMobileRowClick={(record) =>
                                    handleViewTransactions(record)
                                }
                                columns={TreasuryTransactionsSchema}
                                mobileComponent={<TransactionTableMobileCard />}
                                showPagination
                                paging={{
                                    ...transactionFilterOptions,
                                    total: transactionPaging.total
                                }}
                                updatePage={(value) => {
                                    setTransactionFilterOptions({
                                        ...transactionFilterOptions,
                                        page: value
                                    });
                                }}
                                emptyText='No transactions yet'
                            />

                            <UnlinkAccount
                                open={openModal}
                                onClose={() => setOpenModal(false)}
                                selected={account}
                            />
                        </div>
                    </div>
                    {viewDetails && selectedTransaction && (
                        <TreasuryTransactionDetails
                            open={viewDetails}
                            onClose={() => setViewDetails(false)}
                            details={selectedTransaction}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default TransactionsDetails;
